import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import VelavuNotice from "../../../elements/velavu-notice/velavu-notice";
import {
	MappedinCredentialsError,
	VelavuAPI,
	VelavuIntegrationsWithMappedin,
	VelavuMappedinIntegration,
	VelavuMappedinVenue,
} from "velavu-js-api";
import VelavuButton from "../../../elements/velavu-button";
import VelavuInput from "../../../elements/velavu-input";
import * as styles from "./mappedin-integration-settings.module.scss";
import LoadingDots from "../../../elements/loading-dots";
import { VelavuScrollableRoundTable } from "../../../elements/velavu-scrollable-round-table";
import { makeStyleable } from "../../../helper/icon-helper";
import IconDownload from "../../../dynamicicons/icon-download";
import IconRefresh from "../../../dynamicicons/icon-refresh";
import IconUnlink from "../../../dynamicicons/icon-unlink";
import IconSettings from "../../../dynamicicons/icon-settings";
import { Set as ImmutableSet } from "immutable";
import { useVelavuModal } from "../../../elements/velavu-modal";
import VelavuAlertModal from "../../../elements/velavu-alert-modal/velavu-alert-modal";
import VelavuAlertModalTitle from "../../../elements/velavu-alert-modal/velavu-alert-modal-title";
import VelavuAlertModalContent from "../../../elements/velavu-alert-modal/velavu-alert-modal-content";
import VelavuAlertModalActions from "../../../elements/velavu-alert-modal/velavu-alert-modal-actions";
import VelavuAlertModalObject from "../../../elements/velavu-alert-modal/velavu-alert-modal-object";
import ChangeMappedinCredentialsModal from "./change-mappedin-credentials-modal";
import {
	CredentialValidationError,
	credentialValidationErrorToMessage,
} from "./mappedin-credential-validation";
import VelavuMenu, { VelavuMenuItem } from "../../../elements/velavu-menu";
import { VelavuOverflowButton } from "../../../elements/velavu-menu";
import useMenuAnchorState from "../../../state/menu-anchor-state";
import whitelabel from "../../../whitelabel";
import { useMappedinIntegrationContext } from "../../main/mappedin-integration-provider";
import { useHasPermission } from "../../sign-in-context/sign-in-context";
import { getPermissionDeniedMessage } from "../../../helper/permission-helper";

export interface MappedinIntegrationSettingsProps {
	integration: VelavuMappedinIntegration | undefined;
	onUpdate?: (integration: VelavuMappedinIntegration | undefined) => void;
}

export default function MappedinIntegrationSettings(
	props: MappedinIntegrationSettingsProps,
) {
	const { setMappedinIntegration } = useMappedinIntegrationContext();
	const onUpdate = useCallback(
		(integration: VelavuMappedinIntegration | undefined) => {
			setMappedinIntegration(integration ?? null);
			props.onUpdate?.(integration);
		},
		[setMappedinIntegration, props.onUpdate],
	);

	if (props.integration === undefined) {
		return <MappedinIntegrationNone onUpdate={onUpdate} />;
	} else {
		return (
			<MappedinIntegrationEdit
				integration={props.integration}
				onUpdate={onUpdate}
			/>
		);
	}
}

interface MappedinIntegrationNoneProps {
	onUpdate?: (integration: VelavuMappedinIntegration) => void;
}

function MappedinIntegrationNone(props: MappedinIntegrationNoneProps) {
	const { t } = useTranslation();
	const propsOnUpdate = props.onUpdate;

	const [apiKeyInput, setAPIKeyInput] = useState("");
	const [apiSecretInput, setAPISecretInput] = useState("");

	const sanitizedAPIKeyInput = useMemo(() => {
		return apiKeyInput.trim();
	}, [apiKeyInput]);
	const sanitizedSecretInput = useMemo(() => {
		return apiSecretInput.trim();
	}, [apiSecretInput]);
	const inputValid =
		sanitizedAPIKeyInput !== "" && sanitizedSecretInput !== "";

	const canWrite = useHasPermission("integrations:write");

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<CredentialValidationError | null>(null);
	const save = useCallback(async () => {
		//Ignore if the input is invalid
		if (!inputValid || isLoading || !canWrite) {
			return;
		}

		//Reset the state
		setError(null);
		setIsLoading(true);

		//Save the credentials on the backend
		let newIntegration: VelavuIntegrationsWithMappedin;
		try {
			newIntegration =
				await VelavuAPI.integrations.mappedin.addIntegration({
					api_key: sanitizedAPIKeyInput,
					secret: sanitizedSecretInput,
				});
		} catch (err) {
			if (err instanceof MappedinCredentialsError) {
				setError(CredentialValidationError.InvalidCredentials);
			} else {
				setError(CredentialValidationError.ExternalError);
			}
			return;
		} finally {
			setIsLoading(false);
		}

		//Update the integration
		propsOnUpdate?.(newIntegration.mappedin);
	}, [
		inputValid,
		isLoading,
		canWrite,
		setIsLoading,
		setError,
		sanitizedAPIKeyInput,
		sanitizedSecretInput,
		propsOnUpdate,
	]);

	return (
		<div className={styles.container}>
			<span className={styles.title}>
				{t("settings.integrations.mappedin_title")}
			</span>

			<VelavuNotice type="info">
				{t("settings.integrations.mappedin_setup_notice")}
			</VelavuNotice>

			<div className={styles.inputRow}>
				<VelavuInput
					titleText={t("settings.integrations.api_key")}
					placeholder={t("settings.integrations.api_key_placeholder")}
					value={apiKeyInput}
					onChange={setAPIKeyInput}
					disabled={isLoading || !canWrite}
					fullWidth
					bottomPadding
				/>

				<VelavuInput
					titleText={t("settings.integrations.secret")}
					placeholder={t("settings.integrations.secret_placeholder")}
					value={apiSecretInput}
					onChange={setAPISecretInput}
					disabled={isLoading || !canWrite}
					type="password"
					fullWidth
					bottomPadding
				/>
			</div>

			<div className={styles.statusRow}>
				<VelavuButton
					label={t("settings.integrations.save")}
					disabled={isLoading || !inputValid || !canWrite}
					tooltip={
						!canWrite
							? getPermissionDeniedMessage(
									"integrations:write",
									t,
								)
							: undefined
					}
					loadingIndicator={isLoading}
					onClick={save}
				/>

				{error !== null && (
					<VelavuNotice type="error">
						{credentialValidationErrorToMessage(error, t)}
					</VelavuNotice>
				)}
			</div>
		</div>
	);
}

interface MappedinIntegrationEditProps {
	integration: VelavuMappedinIntegration;
	onUpdate?: (integration: VelavuMappedinIntegration | undefined) => void;
}

interface ConnectedVenue {
	venue: VelavuMappedinVenue;
	siteID: string;
}

/// A type of activity performed on a venue
enum ActivityType {
	Import,
	Sync,
	Disconnect,
}

/// The result of an activity performed on a venue
interface ActivityResult {
	type: ActivityType;
	venueID: string;
	venueName: string;
	success: boolean;
}

/// Transforms an ActivityResult into a readable message
function activityResultToMessage(result: ActivityResult, t: TFunction): string {
	if (result.success) {
		switch (result.type) {
			case ActivityType.Import:
				return t("settings.integrations.activity_import_success", {
					name: result.venueName,
				});
			case ActivityType.Sync:
				return t("settings.integrations.activity_sync_success", {
					name: result.venueName,
				});
			case ActivityType.Disconnect:
				return t("settings.integrations.activity_disconnect_success", {
					name: result.venueName,
				});
		}
	} else {
		switch (result.type) {
			case ActivityType.Import:
				return t("settings.integrations.activity_import_failed", {
					name: result.venueName,
				});
			case ActivityType.Sync:
				return t("settings.integrations.activity_sync_failed", {
					name: result.venueName,
				});
			case ActivityType.Disconnect:
				return t("settings.integrations.activity_disconnect_failed", {
					name: result.venueName,
				});
		}
	}
}

function MappedinIntegrationEdit(props: MappedinIntegrationEditProps) {
	const { t } = useTranslation();

	const canWrite = useHasPermission("integrations:write");

	const propsOnUpdate = props.onUpdate;
	const { pushModal } = useVelavuModal();

	//Menu state
	const [menuAnchor, openMenu, closeMenu, useWrapClose] =
		useMenuAnchorState();

	//Fetch available venues
	const [availableVenues, setAvailableVenues] = useState<
		VelavuMappedinVenue[] | null
	>(null);
	useEffect(() => {
		VelavuAPI.integrations.mappedin.getVenues().then(setAvailableVenues);
	}, [setAvailableVenues]);

	//Collect imports to a map of venue IDs to site IDs
	const importsMap = useMemo<ReadonlyMap<string, string>>(() => {
		const map = new Map<string, string>();
		for (const importItem of props.integration.imports) {
			map.set(importItem.venue_id, importItem.site_id);
		}
		return map;
	}, [props.integration.imports]);

	//Sort venues into connected and other
	const [connectedVenues, otherVenues] = useMemo(() => {
		const connected: ConnectedVenue[] = [];
		const other: VelavuMappedinVenue[] = [];

		if (availableVenues !== null) {
			for (const venue of availableVenues) {
				const siteID = importsMap.get(venue.id);
				if (siteID !== undefined) {
					connected.push({
						venue: venue,
						siteID: siteID,
					});
				} else {
					other.push(venue);
				}
			}
		}

		return [connected, other];
	}, [availableVenues, importsMap]);

	const [lastActivityResult, setLastActivityResult] =
		useState<ActivityResult | null>(null);

	const [loadingVenueIDs, setLoadingVenueIDs] =
		useState<ImmutableSet<string>>(ImmutableSet());
	const [isDisconnectingIntegration, setIsDisconnectingIntegration] =
		useState(false);

	const performUpdate = useCallback(
		async (
			venue: VelavuMappedinVenue,
			activityType: ActivityType,
			update: () => Promise<VelavuMappedinIntegration>,
		) => {
			//Ignore if the venue is already loading
			if (loadingVenueIDs.has(venue.id)) {
				return;
			}

			//Set the venue loading state
			setLoadingVenueIDs((prev) => prev.add(venue.id));

			//Perform the update
			let success = false;
			let integration: VelavuMappedinIntegration;
			try {
				integration = await update();
				success = true;
			} catch (err) {
				success = false;
				console.warn(
					`Failed to update venue ${venue.name} (${venue.id})`,
					err,
				);
				return;
			} finally {
				//Clear loading state
				setLoadingVenueIDs((prev) => prev.remove(venue.id));

				//Set the last activity result
				setLastActivityResult({
					type: activityType,
					venueID: venue.id,
					venueName: venue.name,
					success: success,
				});
			}

			//Update the integration
			propsOnUpdate?.(integration);
		},
		[
			loadingVenueIDs,
			setLoadingVenueIDs,
			setLastActivityResult,
			propsOnUpdate,
		],
	);

	const importVenue = useCallback(
		async (venue: VelavuMappedinVenue) => {
			performUpdate(venue, ActivityType.Import, () =>
				VelavuAPI.integrations.mappedin.importVenue(venue.id),
			);
		},
		[performUpdate],
	);

	const syncVenue = useCallback(
		async (venue: VelavuMappedinVenue) => {
			performUpdate(venue, ActivityType.Sync, () =>
				VelavuAPI.integrations.mappedin.importVenue(venue.id),
			);
		},
		[performUpdate],
	);

	const disconnectVenue = useCallback(
		async (venue: VelavuMappedinVenue) => {
			//Show confirmation modal
			const confirmed = await pushModal<boolean>((resolve) => (
				<VelavuAlertModal>
					<VelavuAlertModalTitle>
						{t("settings.integrations.disconnect_venue_title")}
					</VelavuAlertModalTitle>

					<VelavuAlertModalObject>
						{venue.name}
					</VelavuAlertModalObject>

					<VelavuAlertModalContent>
						{t("settings.integrations.disconnect_venue_body")}
					</VelavuAlertModalContent>

					<VelavuAlertModalActions>
						<VelavuButton
							label={t("common.cancel")}
							variant="outlined"
							fullWidth
							onClick={() => resolve(false)}
						/>

						<VelavuButton
							label={t("common.disconnect")}
							variant="normal"
							fullWidth
							onClick={() => resolve(true)}
						/>
					</VelavuAlertModalActions>
				</VelavuAlertModal>
			));

			//Only proceed if user confirmed
			if (!confirmed) {
				return;
			}

			performUpdate(venue, ActivityType.Disconnect, async () => {
				await VelavuAPI.integrations.mappedin.disconnectVenue(venue.id);
				return {
					...props.integration,
					imports: props.integration.imports.filter(
						(importItem) => importItem.venue_id !== venue.id,
					),
				};
			});
		},
		[performUpdate, props.integration, pushModal, t],
	);

	const changeCredentials = useCallback(async () => {
		closeMenu();

		//Show the change credentials modal
		const updated = await pushModal<boolean>((resolve) => (
			<ChangeMappedinCredentialsModal onClose={resolve} />
		));

		//Refresh available venues if credentials were updated
		if (updated) {
			setAvailableVenues(null);
			VelavuAPI.integrations.mappedin
				.getVenues()
				.then(setAvailableVenues);
		}
	}, [closeMenu, pushModal, setAvailableVenues]);

	const disconnectIntegration = useCallback(async () => {
		closeMenu();

		//Ignore if already disconnecting
		if (isDisconnectingIntegration) {
			return;
		}

		//Show confirmation modal
		const confirmed = await pushModal<boolean>((resolve) => (
			<VelavuAlertModal>
				<VelavuAlertModalTitle>
					{t("settings.integrations.disconnect_integration_title")}
				</VelavuAlertModalTitle>

				<VelavuAlertModalContent>
					{t("settings.integrations.disconnect_integration_body")}
				</VelavuAlertModalContent>

				<VelavuAlertModalActions>
					<VelavuButton
						label={t("common.cancel")}
						variant="outlined"
						fullWidth
						onClick={() => resolve(false)}
					/>

					<VelavuButton
						label={t("settings.integrations.disconnect")}
						variant="danger"
						fullWidth
						onClick={() => resolve(true)}
					/>
				</VelavuAlertModalActions>
			</VelavuAlertModal>
		));

		//Only proceed if user confirmed
		if (!confirmed) {
			return;
		}

		//Set loading state
		setIsDisconnectingIntegration(true);

		try {
			//Disconnect the integration
			await VelavuAPI.integrations.mappedin.removeIntegration();
		} catch (err) {
			console.warn("Failed to disconnect Mappedin integration", err);
			return;
		} finally {
			setIsDisconnectingIntegration(false);
		}

		//Update the integration to undefined
		propsOnUpdate?.(undefined);
	}, [
		closeMenu,
		isDisconnectingIntegration,
		setIsDisconnectingIntegration,
		propsOnUpdate,
		pushModal,
		t,
	]);

	return (
		<div className={styles.container}>
			<VelavuMenu
				open={!!menuAnchor}
				onClose={closeMenu}
				refPosition={menuAnchor}
				align="right"
			>
				<VelavuMenuItem
					icon={makeStyleable(IconSettings)}
					label={t("settings.integrations.change_credentials")}
					onClick={changeCredentials}
					disabled={!canWrite}
				/>
				<VelavuMenuItem
					icon={makeStyleable(IconUnlink)}
					label={t("settings.integrations.disconnect")}
					onClick={disconnectIntegration}
					disabled={isDisconnectingIntegration || !canWrite}
					danger
				/>
			</VelavuMenu>

			<div className={styles.titleRow}>
				<span className={styles.title}>
					{t("settings.integrations.mappedin_title")}
				</span>
				<VelavuOverflowButton
					open={!!menuAnchor}
					onClick={openMenu}
					disabled={isDisconnectingIntegration || !canWrite}
					tooltip={
						!canWrite
							? getPermissionDeniedMessage(
									"integrations:write",
									t,
								)
							: undefined
					}
				/>
			</div>

			{availableVenues === null ? (
				<div className={styles.loadingWrapper}>
					<LoadingDots color={whitelabel.palette.secondary500} />
				</div>
			) : (
				<>
					<span className={styles.subtitle}>
						{t("settings.integrations.imported_venues")}
					</span>

					{connectedVenues.length === 0 ? (
						<div className={styles.blankState}>
							{t("settings.integrations.no_imported_venues")}
						</div>
					) : (
						<VelavuScrollableRoundTable
							className={styles.table}
							layout={[
								{ flex: 1 },
								{ width: 72, alignment: "end" },
							]}
						>
							<tbody>
								{connectedVenues.map((entry) => (
									<tr key={entry.venue.id}>
										<td>{entry.venue.name}</td>
										<td>
											<div
												className={
													styles.venueActionButtons
												}
											>
												{loadingVenueIDs.has(
													entry.venue.id,
												) ? (
													<LoadingDots
														color={
															whitelabel.palette
																.secondary700
														}
													/>
												) : (
													<>
														<VelavuButton
															icon={makeStyleable(
																IconRefresh,
															)}
															variant="plain"
															disabled={!canWrite}
															tooltip={
																!canWrite
																	? getPermissionDeniedMessage(
																			"integrations:write",
																			t,
																		)
																	: t(
																			"settings.integrations.sync",
																		)
															}
															onClick={() =>
																syncVenue(
																	entry.venue,
																)
															}
														/>

														<VelavuButton
															icon={makeStyleable(
																IconUnlink,
															)}
															variant="plain"
															disabled={!canWrite}
															tooltip={
																!canWrite
																	? getPermissionDeniedMessage(
																			"integrations:write",
																			t,
																		)
																	: t(
																			"settings.integrations.disconnect_venue",
																		)
															}
															onClick={() =>
																disconnectVenue(
																	entry.venue,
																)
															}
														/>
													</>
												)}
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</VelavuScrollableRoundTable>
					)}

					<span className={styles.subtitle}>
						{t("settings.integrations.venues_available_to_import")}
					</span>

					{otherVenues.length === 0 ? (
						<div className={styles.blankState}>
							{t("settings.integrations.no_venues_available")}
						</div>
					) : (
						<VelavuScrollableRoundTable
							className={styles.table}
							layout={[
								{ flex: 1 },
								{ width: 60, alignment: "end" },
							]}
						>
							<tbody>
								{otherVenues.map((venue) => (
									<tr key={venue.id}>
										<td>{venue.name}</td>
										<td>
											<div
												className={
													styles.venueActionButtons
												}
											>
												{loadingVenueIDs.has(
													venue.id,
												) ? (
													<LoadingDots
														color={
															whitelabel.palette
																.secondary700
														}
													/>
												) : (
													<VelavuButton
														icon={makeStyleable(
															IconDownload,
														)}
														variant="plain"
														disabled={!canWrite}
														tooltip={
															!canWrite
																? getPermissionDeniedMessage(
																		"integrations:write",
																		t,
																	)
																: t(
																		"settings.integrations.import_venue",
																	)
														}
														onClick={() =>
															importVenue(venue)
														}
													/>
												)}
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</VelavuScrollableRoundTable>
					)}
				</>
			)}

			{lastActivityResult !== null && (
				<VelavuNotice
					type={lastActivityResult.success ? "confirm" : "error"}
				>
					{activityResultToMessage(lastActivityResult, t)}
				</VelavuNotice>
			)}
		</div>
	);
}
